import {useState, ChangeEvent} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import {useNavigate} from 'react-router-dom';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import ArrTooltipWrapper from '../../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../../../src/assets/images/info-icon.svg';
import './NewJobConfirmationModal.scss';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../../redux/store';
import {
  resetEditMode,
  resetFormData,
  setCloneJobName,
  setDefineCriteria,
  showToast,
} from '../../../../../../redux/slice/aiReviewSlice';
import {
  useCreateAIReviewJob,
  useSubmitAIReviewJob,
  useUpdateAIReviewJob,
} from '../../../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../../../shared/constants';
import {CRITERIA} from '../issue-coding-type-header/IssueCodingTypeHeader';

interface NewJobConfirmationModalProps {
  showNewJobConfirmationModal: boolean;
  onCloseNewJobConfirmationModal: () => void;
}

const NewJobConfirmationModal = ({
  showNewJobConfirmationModal,
  onCloseNewJobConfirmationModal,
}: NewJobConfirmationModalProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const jobType = useSelector((state: RootState) => state.aiReview.jobType);

  const formData = useSelector((state: RootState) => state.aiReview.formData);
  const [confirmationText, setConfirmationText] = useState<string>('');
  // Validation logic: Button is enabled only if text matches exactly
  const isSubmitDisabled = !/^i\s+understand$/i.test(confirmationText.trim());
  const {mutate: createAIReviewJob} = useCreateAIReviewJob();
  const {mutate: updateAIReviewJob} = useUpdateAIReviewJob();
  const {mutate: submitAIReviewJob} = useSubmitAIReviewJob();

  const handleUnderstandInput = (e: ChangeEvent<HTMLInputElement>) => {
    setConfirmationText(e.target.value);
  };
  const onConfirmationModalCancel = () => {
    onCloseNewJobConfirmationModal();
  };

  const triggerToast = (status: 'Draft' | 'SUBMITTED') => {
    if (status === 'Draft') {
      dispatch(
        showToast({
          title: t('ai.review.newJobDetail.draftToastTitle'),
          message: t('ai.review.newJobDetail.draftToastDesc', {
            value: formData?.jobDetail?.jobName,
          }),
          status: [ToastType.Success],
        })
      );
    } else if (jobType === 'ISSUECODE') {
      dispatch(
        showToast({
          title: t('ai.review.newJobDetail.createToastTitle'),
          message: t('ai.review.newJobDetail.createToastIssueCodeDesc'),
          status: [ToastType.Success],
        })
      );
    } else {
      dispatch(
        showToast({
          title: t('ai.review.newJobDetail.createToastTitle'),
          message: t('ai.review.newJobDetail.createToastAIRelevancyDesc'),
          status: [ToastType.Success],
        })
      );
    }
    onCloseNewJobConfirmationModal();
    navigate('/AI/Review');
    dispatch(setCloneJobName(''));
    dispatch(resetFormData());
    dispatch(resetEditMode());
    dispatch(setDefineCriteria(CRITERIA.MANUAL));
  };

  const handleSaveOrSubmit = (status: 'Draft' | 'SUBMITTED') => {
    const issueCodeData = formData.contextAndCriteria.issueCodes.map((ic) => ({
      code: ic.code,
      question: ic.question,
    }));
    const payload = {
      jobType: jobType,
      jobName: formData.jobDetail.jobName,
      description: formData.jobDetail.description ?? '',
      groupID: Number(formData.jobDetail.selectedGroup?.id || -1),
      caseContext: formData.contextAndCriteria.context,
      issueCodes: issueCodeData,
      groundTruth: formData.groundTruth ?? '',
      relevanceCriteria: formData.contextAndCriteria.relevanceCriteria,
      sourceType: formData.sourceSelectionMode,
      sourceID: formData.sourceSelection ?? 0,
    };

    const mutationOptions = {
      onSuccess: async (data: any) => {
        onCloseNewJobConfirmationModal();
        if (data?.errorMessage) {
          dispatch(
            showToast({
              title: t('common.error'),
              message: data.errorMessage,
              status: [ToastType.Error],
            })
          );
          return;
        }
        if (status === 'Draft') {
          triggerToast(status);
          return;
        }
        await submitAIReviewJob(
          {jobId: data.jobId},
          {
            onSuccess: () => {
              if (data?.errorMessage) {
                onCloseNewJobConfirmationModal();
                dispatch(
                  showToast({
                    title: t('common.error'),
                    message: data.errorMessage,
                    status: [ToastType.Error],
                  })
                );
                return;
              }
              triggerToast(status);
            },
          }
        );
      },
      onError: (error: any) => {
        dispatch(
          showToast({
            title: t('common.error'),
            message:
              error?.response?.data?.detail ??
              error?.message ??
              t('common.error'),
            status: [ToastType.Error],
          })
        );
      },
    };

    if (formData.jobId) {
      updateAIReviewJob(
        {
          ...payload,
          jobId: formData.jobId,
        },
        mutationOptions
      );
    } else {
      createAIReviewJob(payload, mutationOptions);
    }
  };
  const onConfirmationModalSavesAsDraft = () => {
    handleSaveOrSubmit('Draft');
  };

  const onConfirmationModalSubmit = () => {
    handleSaveOrSubmit('SUBMITTED');
  };
  const documentsInfo = () => (
    <>
      <Typography
        className="typo-text subhead5_size"
        data-testid="documents-tooltip-desc"
        tabIndex={0}
        aria-label={t('ai.review.newJobDetail.confirmationModal.toolToopTitle')}
      >
        {t('ai.review.newJobDetail.confirmationModal.toolToopTitle')}
      </Typography>
    </>
  );
  // 1. Data array for easy iteration
  const jobData = [
    {
      label: t('ai.review.newJobDetail.confirmationModal.jobName'),
      value: t('ai.review.newJobDetail.confirmationModal.jobNameValue'),
    },
    {
      label: t('ai.review.newJobDetail.confirmationModal.jobType'),
      value: t('ai.review.newJobDetail.confirmationModal.jobTypeValue'),
    },
    {
      label: t('ai.review.newJobDetail.confirmationModal.jobGroup'),
      value: t('ai.review.newJobDetail.confirmationModal.jobGroupValue'),
    },
    {
      label: t('ai.review.newJobDetail.confirmationModal.sourceSelection'),
      value: t('ai.review.newJobDetail.confirmationModal.sourceSelectionValue'),
    },
  ];
  return (
    <>
      <ArrModal
        className="newJobConfirmationModel"
        showModal={showNewJobConfirmationModal}
        onClose={onCloseNewJobConfirmationModal}
        showClose={false}
        title={t('ai.review.newJobDetail.confirmationModal.title')}
        aria-modal="true"
        aria-labelledby={t('ai.review.newJobDetail.confirmationModal.title')}
        aria-describedby="new-job-confirmation-description"
        dataTestId="new-job-confirmation-modal"
        buttons={[
          {
            id: 'modal-cancel-button',
            label: t('common.cancel'),
            onClick: onConfirmationModalCancel,
            variant: 'text',
            dataTestId: 'njc-modal-cancel-button',
            className: 'modal-cancel-button',
          },
        ]}
      >
        <>
          <Box className="job-list" data-test="job-list-wrapper">
            {jobData &&
              jobData.map((item, _index) => (
                <Box
                  key={item.label}
                  className="list-item-box"
                  data-testid="job-list-box"
                >
                  <Typography
                    className="typo-text body2_size list-label"
                    data-testid="job-list-item-labels"
                    tabIndex={0}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    className="typo-text label1_size list-value"
                    data-testid="job-list-item-label"
                    tabIndex={0}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
          </Box>
          <Box
            className="total-documents-box"
            data-testid="total-documents-box"
          >
            <Box className="tooltip-box">
              <Typography
                className="typo-text subhead3_size"
                data-testid="total-documents-title"
                tabIndex={0}
                aria-label={t(
                  'ai.review.newJobDetail.confirmationModal.totalDocuments'
                )}
              >
                {t('ai.review.newJobDetail.confirmationModal.totalDocuments')}
              </Typography>
              <ArrTooltipWrapper
                title={t('ai.review.newJobDetail.confirmationModal.title')}
                aria-label={t('ai.review.newJobDetail.confirmationModal.title')}
                trigger={
                  <InfoIcon
                    fontSize="small"
                    className="info-icon"
                    data-testid="total-documents-title"
                    aria-label="information"
                    tabIndex={0}
                  />
                }
              >
                {documentsInfo()}
              </ArrTooltipWrapper>
            </Box>
            <Box>
              <Typography
                className="typo-text subhead3_size"
                data-testid="total-documents-value"
                aria-label={t(
                  'ai.review.newJobDetail.confirmationModal.documentsNumber'
                )}
                tabIndex={0}
              >
                {t('ai.review.newJobDetail.confirmationModal.documentsNumber')}
              </Typography>
            </Box>
          </Box>

          <Box className="input-box" data-testid="input-box">
            <Typography
              className="confirmation-modal-description"
              data-testid="confirmation-modal-description"
              aria-describedby={t(
                'ai.review.newJobDetail.confirmationModal.Description'
              )}
              tabIndex={0}
            >
              {t('ai.review.newJobDetail.confirmationModal.Description')}
            </Typography>
            <ArrTextField
              className="understand-input"
              sizeVariant="medium"
              value={confirmationText}
              dataTestId="i-understand-input"
              placeholder={t(
                'ai.review.newJobDetail.confirmationModal.inputPlaceHolderText'
              )}
              onChange={handleUnderstandInput}
            />
          </Box>
          <Box
            className="confirmation-modal-btn-container"
            data-testid="confirmation-modal-btn-container"
          >
            <ArrButton
              variant="secondary"
              size="large"
              disabled={false}
              aria-label={t(
                'ai.review.newJobDetail.confirmationModal.saveAsDraftBtn'
              )}
              dataTestId="modaldraft-btn"
              className="draft-btn"
              onClick={onConfirmationModalSavesAsDraft}
            >
              {t('ai.review.newJobDetail.confirmationModal.saveAsDraftBtn')}
            </ArrButton>
            <ArrButton
              variant="primary"
              size="large"
              aria-label={t('common.submit')}
              dataTestId="submit-btn"
              className="modal-submit-btn"
              disabled={isSubmitDisabled}
              onClick={onConfirmationModalSubmit}
            >
              {t('common.submit')}
            </ArrButton>
          </Box>
        </>
      </ArrModal>
    </>
  );
};

export default NewJobConfirmationModal;
